import { useTranslation } from 'react-i18next';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { Button, Image, Table } from 'antd';
import {
  DeleteOutlined,
  // DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useUIDispatchContext } from 'providers/UI';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import { ColumnProps } from 'antd/lib/table';
import {
  deletePartner,
  fetchPartners,
  PartnerListRow
} from 'services/api/partners';

import useOrgEffect from 'hooks/useOrgEffect';
import { OrganizationAlert, Private } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';

type StoryItemType = PartnerListRow;

const PAGE_LIMIT = 10;

const Partners = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleModal } = useUIDispatchContext();
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const [rows, setRows] = useState<PartnerListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchPartners({
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        setRows(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        // swallow — table renders empty on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const onDeletePartner = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        onConfirm: async () => {
          try {
            await deletePartner(id);
            setRefreshKey(k => k + 1);
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [toggleModal]
  );

  const columns: ColumnProps<StoryItemType>[] = useMemo(
    () => [
      {
        title: t('common.id'),
        dataIndex: 'id',
        width: 50,
        key: 'id'
      },
      {
        title: t('table.logo'),
        dataIndex: 'logo',
        key: 'logo',
        width: 100,
        render: (_, { logo }) => (
          <Image preview={false} src={getFileUrl(logo)} />
        )
      },
      {
        title: t('table.name'),
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: t('partners.code'),
        dataIndex: 'code',
        key: 'code'
      },
      {
        title: t('table.created_at'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: value => dayjs(value).format(DATE_FORMAT)
      },
      {
        title: '',
        dataIndex: 'analytic',
        key: 'analytic',
        align: 'right',
        render: (_, { id }) => (
          <Button
            onClick={() => navigate(`/partner/analytic/${id}`)}
            icon={<EyeOutlined />}
            type="primary"
            ghost
          >
            {t('partners.view_analytics')}
          </Button>
        )
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (id: string) => (
          <ActionMenu
            data={[
              {
                title: t('table.edit'),
                icon: <EditOutlined style={{ fontSize: 16 }} />,
                permission: {
                  route: PERMISSION_ROUTES.partner,
                  action: PERMISSION_ACTIONS.update
                },
                action() {
                  navigate(`/partner/details/${id}`);
                }
              },
              // {
              //   title: t('partners.download'),
              //   icon: <DownloadOutlined style={{ fontSize: 16 }} />,
              //   action() {
              //     navigate(`/partner/details/${id}`);
              //   }
              // },
              {
                title: t('table.delete'),
                icon: <DeleteOutlined style={{ fontSize: 16 }} />,
                permission: {
                  route: PERMISSION_ROUTES.partner,
                  action: PERMISSION_ACTIONS.delete
                },
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                },
                action: () => onDeletePartner(+id)
              }
            ]}
            placement="bottomRight"
          />
        )
      }
    ],
    [navigate, onDeletePartner, t]
  );

  const results = rows;

  return (
    <PageWrapper
      title={t('partners.page_title')}
      extra={[
        <Private
          key="add-service-button"
          route={PERMISSION_ROUTES.partner}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button type="primary" onClick={() => navigate('/partner/details')}>
              <PlusOutlined />
              {t('partners.add_new')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Partners;
